import { App } from '@capacitor/app'
import { memberProfileDisplaySnapshot, reconcileConfirmedProfile } from './lib/memberProfileDisplaySnapshot'
import GolfProgress from './components/GolfProgress'
import LetsPlayRacquetMatch from './components/LetsPlayRacquetMatch'
import MemberAcademyPage from './pages/MemberAcademyPage';
import PortalLoginGateway from '@/components/PortalLoginGateway.jsx'
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom'
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react'
import MemberLayout from './MemberLayout'
import MemberThemeProvider from './MemberThemeProvider'
import ComingSoon from './components/ComingSoon'
import MemberDashboardPage from './pages/MemberDashboardPage'
import MemberProfilePage from './pages/MemberProfilePage'
import MemberAccountPage from './pages/MemberAccountPage'
import MemberPrivanaPayResultPage from './pages/MemberPrivanaPayResultPage'
import MemberPrivanaPayCheckoutOpeningPage from './pages/MemberPrivanaPayCheckoutOpeningPage'
import PublicEventGuestPaymentPage from './pages/PublicEventGuestPaymentPage'
import MemberEventsPage from './pages/MemberEventsPage'
import MemberServicesPage from './pages/MemberServicesPage'
import MemberContactFrontDeskPage from './pages/MemberContactFrontDeskPage'
import MemberFacilitiesPage from './pages/MemberFacilitiesPage'
import MemberLetsPlayPage from './pages/MemberLetsPlayPage'
import LetsPlayHistoryPage from './pages/LetsPlayHistoryPage'
import LetsPlayMomentPage from './pages/LetsPlayMomentPage'
import LetsPlayActivityPage from './pages/LetsPlayActivityPage'
import LetsPlayGolfRound from './components/LetsPlayGolfRound'
import MemberVotingListPage from './pages/MemberVotingListPage'
import MemberVotingDetailPage from './pages/MemberVotingDetailPage'
import PublicVotingPage from './pages/PublicVotingPage'
import MemberLocaleBoundary, { memberLocaleScope } from './components/MemberLocaleBoundary'
import MemberGolfCoursePage from './pages/MemberGolfCoursePage'
import MemberGolfLandingPage from './pages/MemberGolfLandingPage'
import MemberGolfCourseRatesPage from './pages/MemberGolfCourseRatesPage'
import MemberGolfBookingSuccessPage from './pages/MemberGolfBookingSuccessPage'
import MemberOpenGroupsPage from './pages/MemberOpenGroupsPage'
import MemberTeeBookingPage from './pages/MemberTeeBookingPage'
import MemberRacquetBookingPage from './pages/MemberRacquetBookingPage'
import MemberRacquetBookingConfirmPage from './pages/MemberRacquetBookingConfirmPage'
import MemberRacquetBookingSuccessPage from './pages/MemberRacquetBookingSuccessPage'
import MemberSpaServicesPage from './pages/MemberSpaServicesPage'
import MemberSpaServiceAvailabilityPage from './pages/MemberSpaServiceAvailabilityPage'
import MemberSpaBookingConfirmPage from './pages/MemberSpaBookingConfirmPage'
import MemberSpaBookingSuccessPage from './pages/MemberSpaBookingSuccessPage'
import MemberFitnessClassesPage from './pages/MemberFitnessClassesPage'
import MemberFitnessClassAvailabilityPage from './pages/MemberFitnessClassAvailabilityPage'
import MemberFitnessBookingPage from './pages/MemberFitnessBookingPage'
import MemberFitnessBookingSuccessPage from './pages/MemberFitnessBookingSuccessPage'
import MemberAccommodationsPage from './pages/MemberAccommodationsPage'
import MemberAccommodationBookingPage from './pages/MemberAccommodationBookingPage'
import MemberDiningReservationsPage from './pages/MemberDiningReservationsPage'
import MemberReciprocalClubsPage from './pages/MemberReciprocalClubsPage'
import MemberReciprocalGuestPassRequestSuccessPage from './pages/MemberReciprocalGuestPassRequestSuccessPage'
import MemberActivitiesPage from './pages/MemberActivitiesPage'
import MemberActivityDetailPage from './pages/MemberActivityDetailPage'
import MemberManageActivitiesPage from './pages/MemberManageActivitiesPage'
import MemberManageActivityDetailPage from './pages/MemberManageActivityDetailPage'
import './styles.css'
import { getApiBase } from '../utils/apiBase'
import { logoutMember } from './auth'
import { normalizeFocus } from '@/lib/imageFocus'
import { Toaster } from '@/components/ui/toaster'
import { classifyTokenError, useMemberPortalApi, useMemberTokenReadiness } from './api/memberPortalApiClient'
import { useMemberActivitiesApi } from './api/activities'
import { useMemberVotingApi } from './api/voting'
import { applyPortalTheme, DEFAULT_MEMBERS_PORTAL_ACCENT, resolveHeroOverlayOpacity } from './theme'
import { useLivePortalTheme } from './hooks/useLivePortalTheme'
import MobileAppBootstrap from './MobileAppBootstrap'
import { bookingCacheScopeKey, clearUpcomingBookingsScope, mergeUpcomingBooking, reconcileUpcomingBookings, setActiveUpcomingBookingsScope, stripTransientBookingMetadata } from './lib/upcomingBookingsCache'
// Keep this absolute specifier in sync with the Members-mobile Vite alias. A relative
// import bypasses that alias and silently selects the web stub in the native bundle.
import { getMemberMobileAuthBridge } from '@/members-portal/lib/memberMobileAuthBridge'
import {
  MEMBER_PROFILE_RESOLUTION_STATUS,
  createAuth0MemberProfile,
  resolveMemberDisplayProfile,
} from './lib/resolveMemberDisplayProfile'
import {
  getStoredMemberClubCode,
  getStoredMemberOrg,
  isCapacitorRuntime,
  setStoredMemberClubCode,
  setStoredMemberOrg,
  clearStoredMemberOrg,
  readInvalidClubResponse,
  resetInvalidMemberClub,
  clearMemberLocalLogoutMarker,
} from './lib/mobileBootstrap'
import {
  clearMemberDisplayCache,
  readClubDisplayCache,
  readMemberDisplayCache,
  writeClubDisplayCache,
  writeMemberDisplayCache,
} from './lib/memberDisplayCache'
import { isExactMemberRequestCurrent } from './lib/memberRequestIdentity'
import { shouldBlockForClubConfigError } from './lib/clubConfigState'
import { emitBootstrapSummary, markBootstrap, startBootstrapSpan } from './lib/bootstrapTelemetry'
import { useCanonicalIdentityResource } from './lib/canonicalIdentityResource'
import RouteTelemetryLifecycle from './components/RouteTelemetryLifecycle'
import MemberRouteErrorBoundary from './components/MemberRouteErrorBoundary'
import MemberDiagnosticsPage from './pages/MemberDiagnosticsPage'
import { useCanonicalAccountResource } from './lib/accountResource'
import { clearMemberResource } from './lib/resourceCache'
import { emitAuthBootstrapDiagnostic, safeAuthErrorCode } from './lib/authBootstrapDiagnostic'
import { memberPortalApiFetch } from './api/memberPortalApiClient'
import { MEMBERS_MOBILE_CALLBACK_URI, resolveMembersMobileRedirectUri } from './lib/authRedirectUri'
import { CALLBACK_TIMEOUT_MS, getNativeCallbackState, setNativeCallbackState, useNativeCallbackState, validateMembersCallback } from './lib/nativeAuthCallbackLifecycle'

const AUTH0_DOMAIN = import.meta.env.VITE_AUTH0_DOMAIN
const AUTH0_CLIENT_ID =
  import.meta.env.VITE_AUTH0_MEMBER_CLIENT_ID || import.meta.env.VITE_AUTH0_CLIENT_ID
const AUTH0_AUDIENCE = import.meta.env.VITE_AUTH0_AUDIENCE
const AUTH0_MEMBER_WEB_REDIRECT_URI = import.meta.env.VITE_AUTH0_MEMBER_WEB_REDIRECT_URI

// Capacitor Auth0 callbacks use the installed com.privana.members scheme plus the tenant/domain path.
const MOBILE_AUTH_CALLBACK_INFLIGHT_KEY = 'privana_member_callback_inflight'

function createLoginLaunchError(code, cause) {
  const error = new Error(code)
  error.code = code
  if (cause) error.cause = cause
  return error
}

const CONFIRMED_CALLBACK_INFLIGHT_TTL_MS = CALLBACK_TIMEOUT_MS
const UNCONFIRMED_CALLBACK_INFLIGHT_TTL_MS = 10 * 1000

function writeMemberCallbackInflight(status) {
  if (typeof window === 'undefined') return
  window.sessionStorage?.setItem(MOBILE_AUTH_CALLBACK_INFLIGHT_KEY, JSON.stringify({ timestamp: Date.now(), status }))
}

export function clearFalseActiveMemberCallbackInflight() {
  if (typeof window === 'undefined') return false
  const raw = window.sessionStorage?.getItem(MOBILE_AUTH_CALLBACK_INFLIGHT_KEY)
  if (!raw) return false
  try {
    const parsed = JSON.parse(raw)
    const status = parsed?.status
    const timestamp = Number(parsed?.timestamp || 0)
    const staleUnconfirmed = status === 'opened_unconfirmed' && (!timestamp || Date.now() - timestamp > UNCONFIRMED_CALLBACK_INFLIGHT_TTL_MS)
    if (status !== 'opened_confirmed' || staleUnconfirmed) {
      clearMemberCallbackInflight()
      return true
    }
  } catch {
    clearMemberCallbackInflight()
    return true
  }
  return false
}

function normalizeInteractiveLoginError(error) {
  const code = String(error?.code || error?.error || '').toLowerCase()
  if (code === 'browser_plugin_unavailable') return 'browser_plugin_unavailable'
  if (code === 'presentation_unconfirmed') return 'presentation_unconfirmed'
  if (code === 'presentation_timeout') return 'presentation_timeout'
  if (code === 'fallback_unavailable') return 'fallback_unavailable'
  if (code === 'fallback_failed') return 'fallback_failed'
  if (code === 'browser_bridge_missing') return 'browser_bridge_missing'
  if (code === 'browser_open_unavailable') return 'browser_open_unavailable'
  if (code === 'browser_open_failed') return 'browser_open_failed'
  if (code === 'callback_already_active') return 'callback_already_active'
  if (code === 'invalid_login_options') return 'invalid_login_options'
  if (code === 'sdk_rejected') return 'sdk_rejected'
  if (code) return 'unknown_login_failure'
  return safeAuthErrorCode(error) === 'sdk_failure' ? 'unknown_login_failure' : safeAuthErrorCode(error)
}

function getInteractiveLoginDiagnosticState({ routeKey = '', callbackInflight = false, browserPluginAvailable, nativePresentationConfirmed, fallbackAvailable, capacitorPlatform, capacitorCoreVersion, nativeBridgePresent, registeredPluginNames, appPluginAvailable, updaterPluginAvailable } = {}) {
  const bridge = isCapacitorRuntime() ? getMemberMobileAuthBridge() : null
  return {
    routeKey,
    isCapacitor: isCapacitorRuntime(),
    browserBridgePresent: Boolean(bridge),
    browserOpenFunctionPresent: typeof bridge?.openBrowser === 'function',
    ...(browserPluginAvailable === undefined ? {} : { browserPluginAvailable: Boolean(browserPluginAvailable) }),
    ...(nativePresentationConfirmed === undefined ? {} : { nativePresentationConfirmed: Boolean(nativePresentationConfirmed) }),
    ...(fallbackAvailable === undefined ? {} : { fallbackAvailable: Boolean(fallbackAvailable) }),
    ...(capacitorPlatform === undefined ? {} : { capacitorPlatform }),
    ...(capacitorCoreVersion === undefined ? {} : { capacitorCoreVersion }),
    ...(nativeBridgePresent === undefined ? {} : { nativeBridgePresent: Boolean(nativeBridgePresent) }),
    ...(registeredPluginNames === undefined ? {} : { registeredPluginNames }),
    ...(appPluginAvailable === undefined ? {} : { appPluginAvailable: Boolean(appPluginAvailable) }),
    ...(updaterPluginAvailable === undefined ? {} : { updaterPluginAvailable: Boolean(updaterPluginAvailable) }),
    callbackInflight,
  }
}

function isValidHttpsAuthorizeUrl(url) {
  try { return new URL(url).protocol === 'https:' } catch { return false }
}

function removeListenerHandle(handle) {
  try { void handle?.remove?.() } catch { /* listener cleanup must not affect auth launch */ }
}

export async function openMembersAuthBrowser(authorizeUrl, { onDiagnostic } = {}) {
  if (!isCapacitorRuntime()) return { opened: false, runtime: 'web', launchOutcome: 'unavailable' }
  if (!authorizeUrl || typeof authorizeUrl !== 'string' || !isValidHttpsAuthorizeUrl(authorizeUrl)) {
    throw createLoginLaunchError('invalid_login_options')
  }
  const bridge = getMemberMobileAuthBridge()
  if (!bridge) throw createLoginLaunchError('browser_bridge_missing')
  if (typeof bridge.openBrowser !== 'function') throw createLoginLaunchError('browser_open_unavailable')
  const browserPluginAvailable = typeof bridge.isBrowserPluginAvailable === 'function' ? bridge.isBrowserPluginAvailable() : false
  const registry = bridge.getNativePluginRegistryDiagnostic?.()
  if (registry) onDiagnostic?.('interactive_login_plugin_registry', registry)
  onDiagnostic?.('interactive_login_plugin_check', { browserPluginAvailable, fallbackAvailable: false })
  if (!browserPluginAvailable) {
    onDiagnostic?.('interactive_login_plugin_unavailable', { browserPluginAvailable, fallbackAvailable: false, safeErrorCode: 'browser_plugin_unavailable' })
    throw createLoginLaunchError('browser_plugin_unavailable')
  }
  try {
    setNativeCallbackState('awaiting_callback', { startedAt: Date.now() })
    writeMemberCallbackInflight('browser_opened')
    onDiagnostic?.('interactive_login_awaiting_callback', { callbackProcessingState: 'awaiting_callback' })
    Promise.resolve(bridge.addBrowserPageLoadedListener?.(() => {
      onDiagnostic?.('interactive_login_browser_page_loaded', { callbackProcessingState: getNativeCallbackState().state })
    })).catch(() => {})
    Promise.resolve(bridge.addBrowserFinishedListener?.(() => {
      onDiagnostic?.('interactive_login_browser_finished', { callbackProcessingState: getNativeCallbackState().state })
      if (getNativeCallbackState().state !== 'awaiting_callback') return
      setNativeCallbackState('cancelled')
      clearMemberCallbackInflight()
      onDiagnostic?.('interactive_login_cancelled', { callbackProcessingState: 'cancelled', safeErrorCode: 'browser_cancelled' })
    })).catch(() => {})
    window.setTimeout(() => {
      if (getNativeCallbackState().state !== 'awaiting_callback') return
      setNativeCallbackState('cancelled')
      clearMemberCallbackInflight()
      onDiagnostic?.('interactive_login_cancelled', { callbackProcessingState: 'cancelled', safeErrorCode: 'callback_timeout', durationMs: CALLBACK_TIMEOUT_MS })
    }, CALLBACK_TIMEOUT_MS)
    await bridge.openBrowser(authorizeUrl)
    onDiagnostic?.('interactive_login_open_resolved', { browserPluginAvailable, nativePresentationConfirmed: false, fallbackAvailable: false })
    return { opened: true, runtime: bridge.bridgeType || 'capacitor', launchOutcome: 'awaiting_callback' }
  } catch (error) {
    clearMemberCallbackInflight()
    setNativeCallbackState('failed')
    if (error?.code === 'fallback_unavailable' || error?.code === 'fallback_failed') {
      onDiagnostic?.('interactive_login_fallback_failed', { browserPluginAvailable, nativePresentationConfirmed: false, fallbackAvailable: false, safeErrorCode: normalizeInteractiveLoginError(error) })
      throw error
    }
    throw createLoginLaunchError('browser_open_failed', error)
  }
}

function withMobileBrowserOpen(loginOptions, { clubCode, routeKey } = {}) {
  if (!isCapacitorRuntime()) return loginOptions
  return {
    ...loginOptions,
    openUrl: async (url) => {
      const emitLaunchDiagnostic = (event, extra = {}) => {
        const callbackInflight = extra.callbackInflight ?? isMemberCallbackInflight()
        void emitAuthBootstrapDiagnostic(clubCode, { event, ...getInteractiveLoginDiagnosticState({ routeKey, callbackInflight, ...extra }), ...(extra.safeErrorCode ? { safeErrorCode: extra.safeErrorCode } : {}) }, { onceKey: `${event}:${Date.now()}` })
      }
      emitLaunchDiagnostic('interactive_login_open_url_called')
      try {
        return await openMembersAuthBrowser(url, { onDiagnostic: emitLaunchDiagnostic })
      } catch (error) {
        emitLaunchDiagnostic('interactive_login_browser_failed', { callbackInflight: false, safeErrorCode: normalizeInteractiveLoginError(error) })
        throw error
      }
    },
  }
}

export function buildMemberLoginOptions({
  config = {},
  redirectUri,
  clubCode,
  organizationParam,
  audience = AUTH0_AUDIENCE,
  forceAccountChooser = false,
} = {}) {
  const authParams = {
    connection: config.auth0MemberConnectionName,
    redirect_uri: redirectUri,
    ui_mode: 'members',
    audience,
    scope: 'openid profile email offline_access',
    ui_club: clubCode,
    ...(forceAccountChooser ? { prompt: 'login' } : {}),
  }
  if (organizationParam) authParams.organization = organizationParam

  return {
    authorizationParams: authParams,
    appState: { clubCode },
  }
}

export function clearMemberCallbackInflight() {
  if (typeof window === 'undefined') return
  window.sessionStorage?.removeItem(MOBILE_AUTH_CALLBACK_INFLIGHT_KEY)
}

export function isMemberCallbackInflight() {
  if (typeof window === 'undefined') return false
  const raw = window.sessionStorage?.getItem(MOBILE_AUTH_CALLBACK_INFLIGHT_KEY)
  if (!raw) return false
  try {
    const parsed = JSON.parse(raw)
    const timestamp = Number(parsed?.timestamp || 0)
    const status = parsed?.status
    const ttl = status === 'opened_unconfirmed' ? UNCONFIRMED_CALLBACK_INFLIGHT_TTL_MS : CONFIRMED_CALLBACK_INFLIGHT_TTL_MS
    if (!timestamp || Date.now() - timestamp > ttl) {
      clearMemberCallbackInflight()
      return false
    }
    return status === 'opened_confirmed' || status === 'opened_unconfirmed' || status === 'browser_opened'
  } catch {
    clearMemberCallbackInflight()
    return false
  }
}

function RedirectToDashboard() {
  const { clubCode } = useParams()
  return <Navigate to={clubCode ? `/${clubCode}/dashboard` : '/dashboard'} replace />
}

export function MemberSecureSessionRecovery({
  tokenReadiness,
  onRetry,
  onSignInAgain,
  onClearFalseActiveSignIn,
  signInStatus = 'idle',
  signInError = '',
}) {
  const isMissingRefreshToken = tokenReadiness?.errorCode === 'missing_refresh_token'
  const status = tokenReadiness?.status || 'restoring'
  const recoverable = status === 'timed_out' || status === 'failed' || status === 'reauthentication_required'

  if (!recoverable) {
    return (
      <div className="mp-panel mp-secure-session-recovery" aria-busy="true" role="status">
        <div className="mp-panel__body">Restoring your secure session…</div>
      </div>
    )
  }

  return (
    <div className="mp-panel mp-secure-session-recovery" aria-busy="false">
      <div className="mp-panel__body mp-secure-session-recovery__body">
        <p className="mp-eyebrow">Secure session</p>
        <strong>We couldn&apos;t restore your secure session.</strong>
        <p>
          {isMissingRefreshToken
            ? 'Your saved session needs a fresh sign-in before Privana can issue the new secure refresh token.'
            : 'Protected member information is unavailable until your session is restored.'}
        </p>
        <div className="mp-secure-session-recovery__actions">
          <button
            className={isMissingRefreshToken ? 'mp-secondary' : 'mp-primary'}
            type="button"
            onClick={onRetry}
          >
            Retry session
          </button>
          <button
            className={isMissingRefreshToken ? 'mp-primary' : 'mp-secondary'}
            type="button"
            onClick={onSignInAgain}
            disabled={signInStatus === 'opening'}
          >
            {signInStatus === 'opening' ? 'Opening secure sign-in…' : 'Sign in again'}
          </button>
        </div>
        {signInError ? <p role="alert" className="mp-secure-session-recovery__error">{signInError}</p> : null}
        {signInStatus === 'blocked' ? (
          <div role="status" className="mp-secure-session-recovery__status">
            <p>Secure sign-in is already opening. Please complete it in the browser.</p>
            <button className="mp-secondary" type="button" onClick={onClearFalseActiveSignIn}>Sign-in did not appear? Try again</button>
          </div>
        ) : null}
      </div>
    </div>
  )
}

function MemberPostLogoutRedirect() {
  return <Navigate to="/login?logged_out=1" replace />
}

function MemberPortalLoginPage({ redirectUri, clubCode = null }) {
  const { loginWithRedirect } = useAuth0()
  const effectiveClubCode = clubCode || getStoredMemberClubCode() || null

  const startLogin = ({ forceAccountChooser = false } = {}) => {
    loginWithRedirect(withMobileBrowserOpen({
      authorizationParams: {
        redirect_uri: redirectUri,
        audience: AUTH0_AUDIENCE,
        scope: 'openid profile email offline_access',
        ...(forceAccountChooser ? { prompt: 'login' } : {}),
      },
      appState: { clubCode: effectiveClubCode || undefined },
    }))
  }

  return (
    <div className="mp-landing">
      <div className="mp-landing__card">
        <p className="mp-eyebrow">Members portal</p>
        <h2>You are logged out</h2>
        <p>Please sign in to continue.</p>
        <div className="mp-landing__actions">
          <button className="mp-primary" type="button" onClick={() => startLogin()}>
            Sign in
          </button>
        </div>
      </div>
      <div className="mp-landing__media placeholder" />
    </div>
  )
}

export function MemberPortalContent({ config, settings, branding, clubCode, redirectUri, setMemberOrgId }) {
  const auth0Client = useAuth0()
  const { isAuthenticated, isLoading, user, loginWithRedirect } = auth0Client
  const location = useLocation()
  const navigate = useNavigate()
  const routeKey = location.pathname.split('/').filter(Boolean).slice(1).join('/') || 'dashboard'
  const isMobileApp = isCapacitorRuntime()
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const [authRestoreTimedOut, setAuthRestoreTimedOut] = useState(false)
  const [interactiveLoginState, setInteractiveLoginState] = useState({ status: 'idle', error: '' })
  const nativeCallback = useNativeCallbackState()
  useEffect(() => {
    console.info('[members-navigation]', { event: 'portal_navigation_committed', clubCode, routeKey })
  }, [clubCode, routeKey])
  useEffect(() => {
    if (nativeCallback.state === 'cancelled') {
      setInteractiveLoginState({ status: 'idle', error: 'Sign-in was cancelled. You can try again.' })
    }
  }, [nativeCallback.state])
  const cacheScope = useMemo(() => ({ clubCode, memberIdentity: user?.sub }), [clubCode, user?.sub])
  const upcomingCacheScopeKey = bookingCacheScopeKey(cacheScope)
  const cachedDisplay = useMemo(() => {
    const endRead = startBootstrapSpan('member_identity_cache_read', { clubCode })
    const cached = readMemberDisplayCache(cacheScope)?.data || null
    endRead({ cache: cached?.profile ? 'hit' : 'miss', status: 'complete' })
    markBootstrap('cached_profile_decision', { clubCode, cache: cached?.profile ? 'accepted' : 'miss', reason: cached?.profile ? 'exact_identity_match' : 'not_found_or_rejected' })
    return cached
  }, [cacheScope, clubCode])
  const [memberProfileDetails, setMemberProfileDetails] = useState(() => cachedDisplay?.profile || null)
  const [memberProfileResolutionStatus, setMemberProfileResolutionStatus] = useState(
    cachedDisplay?.profile ? MEMBER_PROFILE_RESOLUTION_STATUS.LOADING : MEMBER_PROFILE_RESOLUTION_STATUS.IDLE
  )
  const [upcomingBookings, setUpcomingBookings] = useState(() => cachedDisplay?.upcomingBookings || [])
  const upcomingBookingsRef = useRef(upcomingBookings)
  upcomingBookingsRef.current = upcomingBookings
  const [portalBranding, setPortalBranding] = useState(null)
  const [upcomingLoading, setUpcomingLoading] = useState(true)
  const [upcomingError, setUpcomingError] = useState(null)
  const [managedActivities, setManagedActivities] = useState([])
  const [managedLoading, setManagedLoading] = useState(false)
  const [votingMotions, setVotingMotions] = useState([])
  const [votingLoading, setVotingLoading] = useState(false)
  const { listManagedActivities } = useMemberActivitiesApi()
  const { listOpenMotions } = useMemberVotingApi()

  const heroImageFocus = useMemo(
    () => ({
      x: normalizeFocus(settings?.heroImageFocusX ?? settings?.hero_image_focus_x),
      y: normalizeFocus(settings?.heroImageFocusY ?? settings?.hero_image_focus_y),
    }),
    [settings?.heroImageFocusX, settings?.heroImageFocusY, settings?.hero_image_focus_x, settings?.hero_image_focus_y]
  )

  const organizationParam = useMemo(
    () => config.memberPortalOrgId || config.memberPortalOrgName || null,
    [config.memberPortalOrgId, config.memberPortalOrgName]
  )
  const tokenReadiness = useMemberTokenReadiness({ audience: AUTH0_AUDIENCE, organization: organizationParam, clubCode, enabled: Boolean(isAuthenticated && clubCode && !['awaiting_callback', 'processing_callback'].includes(nativeCallback.state)) })
  const { memberPortalApiGet } = useMemberPortalApi({ audience: AUTH0_AUDIENCE, organization: organizationParam, tokenReadiness })

  const authIdentityKey = useMemo(
    () => `${clubCode || ''}::${user?.sub || ''}::${organizationParam || ''}`,
    [clubCode, organizationParam, user?.sub]
  )
  const confirmedProfile = useRef(null)
  const profileScopeRef = useRef(authIdentityKey)
  profileScopeRef.current = authIdentityKey
  if (confirmedProfile.current?.scope !== authIdentityKey) confirmedProfile.current = null
  const handleConfirmedProfileChange = useCallback((next) => {
    if (!next?.id || profileScopeRef.current !== authIdentityKey) return
    const previous = confirmedProfile.current
    confirmedProfile.current = {
      scope: authIdentityKey, id: next.id,
      patch: { ...(previous?.scope === authIdentityKey && previous?.id === next.id ? previous.patch : {}), ...next },
    }
    setMemberProfileDetails(current => {
      const merged = { ...(current?.id === next.id ? current : {}), ...next }
      writeMemberDisplayCache(cacheScope, { profile: memberProfileDisplaySnapshot(merged) })
      return merged
    })
  }, [authIdentityKey, cacheScope])
  const identity = useCanonicalIdentityResource({
    clubCode,
    subject: user?.sub,
    organization: organizationParam,
    enabled: Boolean(isAuthenticated && clubCode && user?.sub && tokenReadiness.status === 'ready'),
    fetchIdentity: () => memberPortalApiGet(`/api/members-portal/${clubCode}/me/identity-summary`),
  })
  const accountMemberId = identity.data?.id || cachedDisplay?.profile?.id || null
  const accountCacheScope = useMemo(() => ({
    clubCode,
    memberIdentity: accountMemberId ? `${user?.sub || ''}::${organizationParam || ''}::${accountMemberId}` : '',
  }), [accountMemberId, clubCode, organizationParam, user?.sub])
  const accountIdentityKey = `${authIdentityKey}::${accountMemberId || ''}`
  const account = useCanonicalAccountResource({ scope: accountCacheScope, identityKey: accountIdentityKey, enabled: Boolean(isAuthenticated && clubCode && user?.sub && accountMemberId && tokenReadiness.status === 'ready'), memberPortalApiGet })
  const { resource: accountResource, loading: accountLoading, error: accountError, refresh: refreshAccount } = account
  const activeIdentityRef = useRef(null)
  const shellMarkedRef = useRef(false)
  const nameMarkedRef = useRef(false)
  const freshMarkedRef = useRef(false)
  activeIdentityRef.current = { identityKey: authIdentityKey, clubCode, subject: user?.sub || null }
  const isCurrentMemberRequest = useCallback((request, cancelled = false) => {
    return isExactMemberRequestCurrent(activeIdentityRef.current, request, cancelled)
  }, [])

  const memberProfile = useMemo(
    () => createAuth0MemberProfile({ user, clubCode }),
    [clubCode, user]
  )

  const resolvedMemberDisplay = useMemo(
    () =>
      resolveMemberDisplayProfile({
        memberProfile,
        memberProfileDetails,
        memberProfileResolutionStatus,
        user,
        clubCode,
      }),
    [clubCode, memberProfile, memberProfileDetails, memberProfileResolutionStatus, user]
  )

  useEffect(() => {
    if (!shellMarkedRef.current) {
      shellMarkedRef.current = true
      markBootstrap('dashboard_first_shell_render', { clubCode, status: 'rendered' })
    }
    if (!nameMarkedRef.current && resolvedMemberDisplay && !resolvedMemberDisplay.isResolving) {
      nameMarkedRef.current = true
      markBootstrap('first_member_name_render', { clubCode, cache: memberProfileDetails ? 'hit' : 'miss' })
    }
    if (!freshMarkedRef.current && memberProfileResolutionStatus === MEMBER_PROFILE_RESOLUTION_STATUS.RESOLVED) {
      freshMarkedRef.current = true
      markBootstrap('first_fresh_member_profile_render', { clubCode, status: 'rendered' })
      emitBootstrapSummary({ clubCode, status: 'complete' })
    }
  }, [clubCode, memberProfileDetails, memberProfileResolutionStatus, resolvedMemberDisplay])

  const startLogin = async ({ forceAccountChooser = false } = {}) => {
    const diagnosticBase = () => getInteractiveLoginDiagnosticState({ routeKey, callbackInflight: isMemberCallbackInflight() })
    void emitAuthBootstrapDiagnostic(clubCode, { event: 'interactive_login_clicked', ...diagnosticBase() }, { onceKey: `interactive-clicked:${Date.now()}` })
    if (interactiveLoginState.status === 'opening') return
    if (isMemberCallbackInflight()) {
      setInteractiveLoginState({ status: 'blocked', error: '' })
      void emitAuthBootstrapDiagnostic(clubCode, { event: 'interactive_login_blocked_active_callback', ...diagnosticBase(), safeErrorCode: 'callback_already_active' }, { onceKey: `interactive-blocked:${Date.now()}` })
      return
    }
    setInteractiveLoginState({ status: 'opening', error: '' })
    if (clubCode) {
      setStoredMemberClubCode(clubCode)
    }

    try {
      const loginOptions = buildMemberLoginOptions({
        config,
        redirectUri,
        clubCode,
        organizationParam,
        forceAccountChooser,
      })
      void emitAuthBootstrapDiagnostic(clubCode, { event: 'interactive_login_options_built', ...diagnosticBase() }, { onceKey: `interactive-options:${Date.now()}` })
      void emitAuthBootstrapDiagnostic(clubCode, { event: 'interactive_login_sdk_started', ...diagnosticBase() }, { onceKey: `interactive-sdk-started:${Date.now()}` })
      await loginWithRedirect(withMobileBrowserOpen(loginOptions, { clubCode, routeKey }))
      setInteractiveLoginState({ status: 'idle', error: '' })
    } catch (error) {
      clearMemberCallbackInflight()
      const safeErrorCode = normalizeInteractiveLoginError(error)
      setInteractiveLoginState({ status: 'idle', error: safeErrorCode === 'browser_plugin_unavailable' ? 'Secure sign-in needs an app update. Please update Privana and try again.' : 'We couldn’t open secure sign-in. Please try again.' })
      void emitAuthBootstrapDiagnostic(clubCode, { event: 'interactive_login_sdk_failed', ...diagnosticBase(), safeErrorCode: safeErrorCode === 'unknown_login_failure' ? 'sdk_rejected' : safeErrorCode }, { onceKey: `interactive-sdk-failed:${Date.now()}` })
    }
  }

  const handleClearFalseActiveSignIn = useCallback(() => {
    if (clearFalseActiveMemberCallbackInflight()) {
      setInteractiveLoginState({ status: 'idle', error: '' })
      void emitAuthBootstrapDiagnostic(clubCode, { event: 'interactive_login_false_active_cleared', ...getInteractiveLoginDiagnosticState({ routeKey, callbackInflight: false }) }, { onceKey: `interactive-false-active-cleared:${Date.now()}` })
    }
  }, [clubCode, routeKey])

  const clearMemberSessionState = useCallback(() => {
    setMemberProfileDetails(null)
    setMemberProfileResolutionStatus(MEMBER_PROFILE_RESOLUTION_STATUS.IDLE)
    setUpcomingError(null)
    setUpcomingBookings([])
    clearUpcomingBookingsScope(cacheScope)
    setManagedActivities([])
    setVotingMotions([])
    account.clear()
    clearMemberResource(cacheScope, 'account-current-statement')
    clearMemberResource(cacheScope, 'club-news')
    clearMemberResource(cacheScope, 'frontdesk-conversation-summary')
    if (typeof window !== 'undefined') {
      try {
        window.localStorage?.removeItem('privana_member_profile')
        window.localStorage?.removeItem('privana_member_theme')
        window.sessionStorage?.removeItem('privana_member_profile')
      } catch { /* storage availability must not block logout */ }
    }
  }, [account, cacheScope])

  useEffect(() => {
    setActiveUpcomingBookingsScope(cacheScope)
    return () => setActiveUpcomingBookingsScope(null)
  }, [cacheScope])

  useEffect(() => {
    if (!user?.sub) {
      setMemberProfileDetails(null)
      setUpcomingError(null)
      setUpcomingBookings([])
      return
    }
    const cached = readMemberDisplayCache(cacheScope)?.data
    setMemberProfileDetails(cached?.profile || null)
    setUpcomingBookings(Array.isArray(cached?.upcomingBookings) ? cached.upcomingBookings : [])
  }, [authIdentityKey, cacheScope, user?.sub])

  const handleLogout = async () => {
    if (isLoggingOut) return
    setIsLoggingOut(true)
    clearMemberSessionState()
    clearMemberDisplayCache(cacheScope)
    try {
      await logoutMember(auth0Client, clubCode, organizationParam, { navigate, setMemberOrgId, memberIdentity: user?.sub })
    } finally {
      setIsLoggingOut(false)
    }
  }

  useEffect(() => {
    if (!isAuthenticated || !clubCode || tokenReadiness.status !== 'ready' || identity.loading) {
      if (identity.loading) setMemberProfileResolutionStatus(MEMBER_PROFILE_RESOLUTION_STATUS.LOADING)
      else {
      setMemberProfileDetails(null)
      setMemberProfileResolutionStatus(MEMBER_PROFILE_RESOLUTION_STATUS.IDLE)
      }
      return undefined
    }
    if (identity.data) {
      const next = reconcileConfirmedProfile(identity.data, confirmedProfile.current, authIdentityKey)
      setMemberProfileDetails(next)
      writeMemberDisplayCache(cacheScope, { profile: memberProfileDisplaySnapshot(next) })
      setMemberProfileResolutionStatus(MEMBER_PROFILE_RESOLUTION_STATUS.RESOLVED)
    } else if (identity.error) setMemberProfileResolutionStatus(MEMBER_PROFILE_RESOLUTION_STATUS.FAILED)
  }, [authIdentityKey, cacheScope, clubCode, identity.data, identity.error, identity.loading, isAuthenticated, tokenReadiness.status])


  useEffect(() => {
    if (!isAuthenticated || !clubCode || tokenReadiness.status !== 'ready') return

    let cancelled = false

    const requestIdentityKey = authIdentityKey

    async function loadVotingMotions() {
      setVotingLoading(true)
      try {
        const motions = await listOpenMotions(clubCode)
        if (!cancelled && requestIdentityKey === authIdentityKey) {
          setVotingMotions(Array.isArray(motions) ? motions : [])
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[member-portal] failed to fetch voting motions', error)
        }
        if (!cancelled && requestIdentityKey === authIdentityKey) {
          setVotingMotions([])
        }
      } finally {
        if (!cancelled) setVotingLoading(false)
      }
    }

    loadVotingMotions()
    return () => {
      cancelled = true
    }
  }, [authIdentityKey, clubCode, isAuthenticated, listOpenMotions, tokenReadiness.status])

  useEffect(() => {
    if (!isAuthenticated || !clubCode || tokenReadiness.status !== 'ready') return

    let cancelled = false

    const requestIdentityKey = authIdentityKey

    async function loadManagedActivities() {
      setManagedLoading(true)
      try {
        const activities = await listManagedActivities(clubCode)
        if (!cancelled && requestIdentityKey === authIdentityKey) {
          setManagedActivities(Array.isArray(activities) ? activities : [])
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[member-portal] failed to fetch managed activities', error)
        }
        if (!cancelled && requestIdentityKey === authIdentityKey) {
          setManagedActivities([])
        }
      } finally {
        if (!cancelled) setManagedLoading(false)
      }
    }

    loadManagedActivities()
    return () => {
      cancelled = true
    }
  }, [authIdentityKey, clubCode, isAuthenticated, listManagedActivities, tokenReadiness.status])

  const loadUpcomingBookings = useCallback(async ({ cancelledRef = null } = {}) => {
    const request = {
      identityKey: activeIdentityRef.current?.identityKey,
      clubCode: activeIdentityRef.current?.clubCode,
      subject: activeIdentityRef.current?.subject,
      cacheScope: { clubCode: activeIdentityRef.current?.clubCode, memberIdentity: activeIdentityRef.current?.subject },
    }
    if (!isAuthenticated || !request.clubCode || !request.subject || tokenReadiness.status !== 'ready') return
    const controller = new AbortController()
    if (cancelledRef) cancelledRef.abort = () => controller.abort()
    setUpcomingLoading(true)
    setUpcomingError(null)
    const endRequest = startBootstrapSpan('upcoming_bookings_request', { clubCode: request.clubCode, category: 'bookings' })
    try {
      const res = await memberPortalApiGet(
        `/api/members-portal/${request.clubCode}/me/upcoming?limit=100&offset=0`,
        { signal: controller.signal }
      )
      const json = await res.json()

      if (!isCurrentMemberRequest(request, cancelledRef?.cancelled)) return
      if (json?.ok) {
        const items = Array.isArray(json.data?.items) ? json.data.items : []
        console.info('[member-portal] upcoming bookings API response', {
          totalCount: json.data?.totalCount ?? json.data?.total_count ?? null,
          itemsLength: items.length,
        })
        const reconciled = reconcileUpcomingBookings(items, upcomingBookingsRef.current, { scopeKey: upcomingCacheScopeKey })
        setUpcomingBookings(reconciled)
        if (isCurrentMemberRequest(request, cancelledRef?.cancelled)) {
          writeMemberDisplayCache(request.cacheScope, { upcomingBookings: stripTransientBookingMetadata(reconciled) })
        }
      } else {
        setUpcomingError('request_failed')
      }
      endRequest({ status: json?.ok ? 'success' : 'failed', reason: json?.ok ? 'none' : 'invalid_response' })
    } catch (error) {
      if (error?.name !== 'AbortError' && process.env.NODE_ENV === 'development') {
        console.warn('[member-portal] failed to fetch upcoming bookings', error)
      }
      if (error?.name !== 'AbortError' && isCurrentMemberRequest(request, cancelledRef?.cancelled)) setUpcomingError('request_failed')
      // A transient refresh failure must never erase usable stale content.
      endRequest({ status: error?.name === 'AbortError' ? 'cancelled' : 'failed', reason: error?.name === 'AbortError' ? 'identity_changed' : 'request_failed' })
    } finally {
      if (isCurrentMemberRequest(request, cancelledRef?.cancelled)) {
        setUpcomingLoading(false)
      }
    }
  }, [isAuthenticated, isCurrentMemberRequest, memberPortalApiGet, tokenReadiness.status, upcomingCacheScopeKey])

  useEffect(() => {
    const refresh = (event) => {
      if (event?.detail?.clubCode && String(event.detail.clubCode) !== String(clubCode)) return
      if (event?.detail?.scopeKey && event.detail.scopeKey !== upcomingCacheScopeKey) return
      void loadUpcomingBookings()
    }
    const commit = (event) => {
      if (event.detail?.scopeKey !== upcomingCacheScopeKey) return
      const booking = { ...event.detail?.booking, _committedGeneration: event.detail?.generation }
      setUpcomingBookings((current) => {
        const next = mergeUpcomingBooking(current, booking)
        writeMemberDisplayCache(cacheScope, { upcomingBookings: stripTransientBookingMetadata(next) })
        return next
      })
    }
    // Refresh private media on foreground return, independently of image errors.
    // This application data owner coalesces web/native notifications; the
    // dashboard greeting owns only its clock and never refreshes bookings.
    let lastResume = null
    let disposed = false
    const resume = () => {
      if (disposed || document.visibilityState !== 'visible' || (lastResume !== null && Date.now() - lastResume < 1000)) return
      lastResume = Date.now()
      void loadUpcomingBookings()
    }
    let appListener
    if (isCapacitorRuntime()) {
      App.addListener('appStateChange', ({ isActive }) => { if (isActive) resume() }).then(listener => {
        if (disposed) void listener.remove()
        else appListener = listener
      }).catch(() => {})
    }
    document.addEventListener('visibilitychange', resume)
    window.addEventListener('focus', resume)
    window.addEventListener('members:upcoming-bookings-invalidated', refresh)
    window.addEventListener('members:booking-committed', commit)
    return () => {
      disposed = true
      void appListener?.remove()
      document.removeEventListener('visibilitychange', resume)
      window.removeEventListener('focus', resume)
      window.removeEventListener('members:upcoming-bookings-invalidated', refresh)
      window.removeEventListener('members:booking-committed', commit)
    }
  }, [cacheScope, clubCode, loadUpcomingBookings, upcomingCacheScopeKey])

  useEffect(() => {
    if (!isAuthenticated || !clubCode || tokenReadiness.status !== 'ready') return
    const cancelledRef = { cancelled: false }
    loadUpcomingBookings({ cancelledRef })
    return () => {
      cancelledRef.cancelled = true
      cancelledRef.abort?.()
    }
  }, [authIdentityKey, clubCode, isAuthenticated, loadUpcomingBookings, tokenReadiness.status])

  useEffect(() => {
    if (!clubCode) return
    let cancelled = false
    fetch(`/api/public/clubs/${encodeURIComponent(clubCode)}/portal-branding`)
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => { if (!cancelled && json?.ok) setPortalBranding(json.data || null) })
      .catch(() => { if (!cancelled) setPortalBranding(null) })
    return () => { cancelled = true }
  }, [clubCode])

  useEffect(() => {
    if (isLoading || isAuthenticated) return
    if (isMemberCallbackInflight()) {
      console.info('[members-mobile-auth] loginWithRedirect skipped while callback is in-flight')
    }
    // Do not automatically call loginWithRedirect for unauthenticated club URLs.
    // The public login card below lets users explicitly sign in or switch users.
  }, [isLoading, isAuthenticated])



  useEffect(() => {
    if (!isLoading) { setAuthRestoreTimedOut(false); return undefined }
    const timeout = window.setTimeout(() => setAuthRestoreTimedOut(true), 8000)
    return () => window.clearTimeout(timeout)
  }, [isLoading])

  if (isAuthenticated && tokenReadiness.status !== 'ready') {
    return (
      <MemberLayout branding={branding} clubCode={clubCode} member={resolvedMemberDisplay} managedActivities={[]} hasVotingAccess={false} isMobileApp={isMobileApp}>
        <MemberSecureSessionRecovery
          tokenReadiness={tokenReadiness}
          onRetry={tokenReadiness.retry}
          signInStatus={interactiveLoginState.status}
          signInError={interactiveLoginState.error}
          onSignInAgain={() => {
            // Existing mobile users can have Auth0 SPA sessions created before
            // offline_access was enabled. missing_refresh_token cannot be fixed
            // silently; a new interactive login is required to obtain it.
            startLogin({ forceAccountChooser: true })
          }}
          onClearFalseActiveSignIn={handleClearFalseActiveSignIn}
        />
      </MemberLayout>
    )
  }

  if (isLoading) {
    if (isMobileApp && getStoredMemberClubCode()) {
      return (
        <MemberLayout branding={branding} clubCode={clubCode} member={null} managedActivities={[]} hasVotingAccess={false} isMobileApp>
          <div className="mp-panel" aria-busy={!authRestoreTimedOut}>
            <div className="mp-panel__body">
              {authRestoreTimedOut ? (
                <><strong>We couldn&apos;t restore your session.</strong><p>Your saved navigation remains available, but protected information is unavailable.</p><div className="mp-landing__actions"><button className="mp-primary" type="button" onClick={() => window.location.reload()}>Retry session</button><button type="button" onClick={() => startLogin({ forceAccountChooser: true })}>Sign in again</button></div></>
              ) : 'Restoring your session…'}
            </div>
          </div>
        </MemberLayout>
      )
    }
    return <div className="member-loading">Preparing login…</div>
  }

  if (!isAuthenticated) {
    return (
      <PortalLoginGateway
        portalType="member"
        clubCode={clubCode}
        clubName={portalBranding?.clubName || portalBranding?.club_name || branding?.clubName || branding?.club_name}
        clubLogoUrl={portalBranding?.clubLogoUrl || portalBranding?.club_logo_url || branding?.logoUrl}
        loginImageUrl={portalBranding?.portalLoginImageUrl || portalBranding?.portal_login_image_url}
        onSignIn={startLogin}
      />
    )
  }

  return (
    <MemberLocaleBoundary
      scope={memberLocaleScope({ clubCode, subject: user?.sub, organization: organizationParam,
        context: [window.location.origin, AUTH0_DOMAIN, AUTH0_CLIENT_ID, AUTH0_AUDIENCE, getApiBase()].join('|') })}
      profile={identity.data ? { ...identity.data, ...confirmedProfile.current?.patch } : confirmedProfile.current?.patch || null}
      settled={Boolean(identity.error || identity.data)}
      clubLocale={(settings?.clubCode === clubCode ? settings.clubDefaultLocale : null) || config.default_locale || settings?.club_default_locale}
    >
    <RouteTelemetryLifecycle pathname={location.pathname} routeKey={routeKey} clubCode={clubCode} />
    <MemberLayout
      branding={branding}
      clubCode={clubCode}
      member={resolvedMemberDisplay}
      managedActivities={managedActivities}
      hasVotingAccess={votingMotions.length > 0}
      isMobileApp={isMobileApp}
      isLoggingOut={isLoggingOut}
      onLogout={handleLogout}
    >
      <MemberRouteErrorBoundary routeKey={routeKey} clubCode={clubCode} key={location.pathname}>
      <Routes>
        <Route
          path="dashboard"
          element={
            <MemberDashboardPage
              member={resolvedMemberDisplay}
              heroImageUrl={settings?.heroImageUrl || settings?.hero_image_url}
              heroImageFocus={heroImageFocus}
              clubLogoUrl={settings?.clubLogoUrl || null}
              clubCode={clubCode}
              upcomingBookings={upcomingBookings}
              upcomingLoading={upcomingLoading}
              upcomingError={upcomingError}
              onUpcomingBookingsRefresh={loadUpcomingBookings}
              displayCacheScope={cacheScope}
              isMobileApp={isMobileApp}
              summary={{
                ...settings?.summary,
                balance: accountResource?.totalAmount,
                outstandingBalance: accountResource?.totalAmount,
                accountError,
                accountLoading,
                currency: { code: settings?.currencyCode || settings?.summary?.currency?.code, symbol: settings?.currencySymbol || settings?.summary?.currency?.symbol },
              }}
              heroOverlayOpacity={settings?.heroOverlayOpacity ?? settings?.hero_overlay_opacity}
            />
          }
        />
        <Route path="book-facilities" element={<MemberFacilitiesPage clubCode={clubCode} />} />
        <Route path="lets-play" element={<MemberLetsPlayPage clubCode={clubCode} />} />
        <Route path="lets-play/history" element={<LetsPlayHistoryPage clubCode={clubCode} />} />
        <Route path="lets-play/activities/:activityId/moment" element={<LetsPlayMomentPage clubCode={clubCode} />} />
        <Route path="lets-play/activities/:activityId/complete" element={<LetsPlayActivityPage clubCode={clubCode} completion />} />
        <Route path="lets-play/activities/:activityId" element={<LetsPlayActivityPage clubCode={clubCode} />} />
        <Route path="lets-play/:sportSlug" element={<MemberLetsPlayPage clubCode={clubCode} />} />
        {['tennis','padel','pickleball','squash'].map(sport => <Route key={sport} path={`lets-play/${sport}/match/:matchId`} element={<LetsPlayRacquetMatch clubCode={clubCode} />} />)}
        <Route path="lets-play/golf/progress" element={<GolfProgress clubCode={clubCode} />} />
        <Route path="lets-play/golf/round/:roundId" element={<LetsPlayGolfRound clubCode={clubCode} />} />
        <Route path="developer-diagnostics" element={<MemberDiagnosticsPage />} />
        <Route
          path="voting"
          element={
            votingLoading && votingMotions.length === 0 ? (
              <div className="mp-panel"><div className="mp-panel__body">Checking voting access…</div></div>
            ) : (
              <MemberVotingListPage clubCode={clubCode} initialMotions={votingMotions} />
            )
          }
        />
        <Route path="voting/:motionId" element={<MemberVotingDetailPage clubCode={clubCode} />} />
        <Route path="academy" element={<MemberAcademyPage clubCode={clubCode} />} />
        <Route path="activities" element={<MemberActivitiesPage clubCode={clubCode} />} />
        <Route
          path="activities/:activityId"
          element={<MemberActivityDetailPage clubCode={clubCode} member={resolvedMemberDisplay} />}
        />
        <Route
          path="manage"
          element={
            managedLoading && managedActivities.length === 0 ? (
              <div className="mp-panel"><div className="mp-panel__body">Checking manage access…</div></div>
            ) : managedActivities.length > 0 ? (
              <MemberManageActivitiesPage clubCode={clubCode} activities={managedActivities} />
            ) : (
              <Navigate to={`/${clubCode}/dashboard`} replace />
            )
          }
        />
        <Route
          path="manage/activities/:activityId"
          element={
            managedLoading && managedActivities.length === 0 ? (
              <div className="mp-panel"><div className="mp-panel__body">Checking manage access…</div></div>
            ) : managedActivities.length > 0 ? (
              <MemberManageActivityDetailPage
                clubCode={clubCode}
                managedActivities={managedActivities}
              />
            ) : (
              <Navigate to={`/${clubCode}/dashboard`} replace />
            )
          }
        />
        <Route
          path="book-racquet/:sportType"
          element={<MemberRacquetBookingPage clubCode={clubCode} isMobileApp={isMobileApp} />}
        />
        <Route
          path="book-racquet/:sportType/open-match/:bookingId/manage"
          element={<MemberRacquetBookingPage clubCode={clubCode} isMobileApp={isMobileApp} />}
        />
        <Route
          path="book-racquet/:sportType/book"
          element={
            <MemberRacquetBookingConfirmPage
              clubCode={clubCode}
              member={resolvedMemberDisplay}
              isMobileApp={isMobileApp}
            />
          }
        />
        <Route
          path="book-racquet/:sportType/booking/success"
          element={<MemberRacquetBookingSuccessPage clubCode={clubCode} />}
        />
        <Route path="golf" element={<MemberGolfLandingPage clubCode={clubCode} />} />
        <Route path="golf/:courseId" element={<MemberGolfCoursePage clubCode={clubCode} />} />
        <Route path="golf/:courseId/rates" element={<MemberGolfCourseRatesPage clubCode={clubCode} />} />
        <Route path="golf/:courseId/open-groups" element={<MemberOpenGroupsPage clubCode={clubCode} />} />
        <Route
          path="golf/:courseId/book/:teeTimeId"
          element={<MemberTeeBookingPage clubCode={clubCode} member={resolvedMemberDisplay} />}
        />
        <Route
          path="golf/booking/success"
          element={<MemberGolfBookingSuccessPage clubCode={clubCode} />}
        />
        <Route
          path="fitness"
          element={<MemberFitnessClassesPage clubCode={clubCode} />}
        />
        <Route
          path="fitness/:classId"
          element={<MemberFitnessClassAvailabilityPage clubCode={clubCode} />}
        />
        <Route
          path="fitness/:classId/book"
          element={<MemberFitnessBookingPage clubCode={clubCode} member={resolvedMemberDisplay} />}
        />
        <Route
          path="fitness/booking/success"
          element={<MemberFitnessBookingSuccessPage clubCode={clubCode} />}
        />
        <Route path="spa" element={<MemberSpaServicesPage clubCode={clubCode} />} />
        <Route path="spa/:serviceId" element={<MemberSpaServiceAvailabilityPage clubCode={clubCode} />} />
        <Route path="spa/:serviceId/confirm" element={<MemberSpaBookingConfirmPage clubCode={clubCode} member={resolvedMemberDisplay} />} />
        <Route path="spa/booking/success" element={<MemberSpaBookingSuccessPage clubCode={clubCode} />} />
        <Route
          path="account"
          element={<MemberAccountPage clubCode={clubCode} summaryCurrency={{ code: settings?.currencyCode || settings?.summary?.currency?.code, symbol: settings?.currencySymbol || settings?.summary?.currency?.symbol }} heroImageUrl={settings?.heroImageUrl || settings?.hero_image_url} heroImageFocus={heroImageFocus} accountResource={accountResource} accountLoading={accountLoading} accountError={accountError} onAccountRefresh={refreshAccount} isMobileApp={isMobileApp} />}
        />
        <Route path="payment-received" element={<MemberPrivanaPayResultPage clubCode={clubCode} state="received" />} />
        <Route path="payment-cancelled" element={<MemberPrivanaPayResultPage clubCode={clubCode} state="cancelled" />} />
        <Route path="payment-declined" element={<MemberPrivanaPayResultPage clubCode={clubCode} state="declined" />} />
        <Route
          path="profile"
          element={
            <MemberProfilePage
              key={authIdentityKey}
              identityScope={authIdentityKey}
              branding={branding}
              clubCode={clubCode}
              member={resolvedMemberDisplay}
              onMemberProfileChange={handleConfirmedProfileChange}
              onLogout={handleLogout}
              isLoggingOut={isLoggingOut}
              isMobileApp={isMobileApp}
            />
          }
        />
        <Route path="events" element={<MemberEventsPage />} />
        <Route path="dining" element={<MemberDiningReservationsPage clubCode={clubCode} isMobilePresentation={isMobileApp} />} />
        <Route path="reciprocal-clubs" element={<MemberReciprocalClubsPage clubCode={clubCode} />} />
        <Route
          path="reciprocal-clubs/request/success"
          element={<MemberReciprocalGuestPassRequestSuccessPage clubCode={clubCode} />}
        />
        <Route path="social-clubs" element={<Navigate to={`/${clubCode}/reciprocal-clubs`} replace />} />
        <Route path="accommodations" element={<MemberAccommodationsPage clubCode={clubCode} />} />
        <Route path="accommodations/book" element={<MemberAccommodationBookingPage clubCode={clubCode} />} />
        <Route path="services" element={<MemberServicesPage />} />
        <Route path="contact-front-desk" element={<MemberContactFrontDeskPage clubCode={clubCode} />} />
        <Route path="" element={<RedirectToDashboard />} />
        <Route path="*" element={<RedirectToDashboard />} />
      </Routes>
      </MemberRouteErrorBoundary>
    </MemberLayout>
    </MemberLocaleBoundary>
  )
}

function AuthenticatedMemberPortal({ config, settings, branding, clubCode, redirectUri, setMemberOrgId }) {
  return (
    <MemberThemeProvider branding={branding}>
      <MemberPortalContent
        config={config}
        settings={settings}
        branding={branding}
        clubCode={clubCode}
        redirectUri={redirectUri}
        setMemberOrgId={setMemberOrgId}
      />
      <Toaster />
    </MemberThemeProvider>
  )
}

function ClubPortalRoute({ redirectUri, setMemberOrgId }) {
  const { clubCode } = useParams()
  if (isCapacitorRuntime() && getStoredMemberClubCode() !== clubCode) {
    return <Navigate to="/app" replace />
  }
  return <ClubPortalContainer redirectUri={redirectUri} setMemberOrgId={setMemberOrgId} />
}

export function NativeAuthCallbackCoordinator() {
  const { handleRedirectCallback, isAuthenticated, isLoading } = useAuth0()
  const navigate = useNavigate()
  const processingRef = useRef(false)
  const pendingRouteRef = useRef(null)
  const callbackState = useNativeCallbackState()

  const diagnostic = useCallback((event, fields = {}) => {
    // Deliberately never include the URL or Auth0 parameters here.
    console.info('[members-mobile-auth]', { event, callbackProcessingState: getNativeCallbackState().state, ...fields })
  }, [])

  useEffect(() => {
    if (!isCapacitorRuntime()) return
    const bridge = getMemberMobileAuthBridge()
    let disposed = false
    let listenerHandle

    const receive = async (rawUrl, callbackSource) => {
      diagnostic(callbackSource === 'launch_url' ? 'auth_callback_launch_url_received' : 'auth_callback_app_url_received', { callbackSource })
      const callbackShapeValid = validateMembersCallback(rawUrl)
      if (!callbackShapeValid) {
        diagnostic('auth_callback_processing_failed', { callbackSource, callbackShapeValid, safeErrorCode: 'callback_shape_invalid' })
        return
      }
      diagnostic('auth_callback_validated', { callbackSource, callbackShapeValid: true })
      if (processingRef.current || ['processing_callback', 'completed'].includes(getNativeCallbackState().state)) {
        diagnostic('auth_callback_duplicate_ignored', { callbackSource, callbackShapeValid: true, safeErrorCode: 'callback_duplicate' })
        return
      }
      processingRef.current = true
      setNativeCallbackState('processing_callback')
      diagnostic('auth_callback_processing_started', { callbackSource, callbackShapeValid: true })
      let browserClosed = false
      try {
        try { await bridge.closeBrowser?.(); browserClosed = true } catch { browserClosed = false }
        diagnostic('auth_callback_browser_closed', { callbackSource, browserClosed })
        const result = await handleRedirectCallback(rawUrl)
        const appStateClub = result?.appState?.clubCode
        const clubCode = /^\d{6}$/.test(String(appStateClub || '')) ? appStateClub : getStoredMemberClubCode()
        pendingRouteRef.current = /^\d{6}$/.test(String(clubCode || '')) ? `/${clubCode}/dashboard` : '/app'
        clearMemberCallbackInflight()
        setNativeCallbackState('completed')
        diagnostic('auth_callback_processing_succeeded', { callbackSource, browserClosed })
      } catch (error) {
        clearMemberCallbackInflight()
        setNativeCallbackState('failed')
        diagnostic('auth_callback_processing_failed', { callbackSource, browserClosed, safeErrorCode: String(error?.error || '').includes('state') ? 'callback_transaction_missing' : 'callback_processing_failed' })
      } finally {
        processingRef.current = false
      }
    }

    Promise.resolve(bridge.addAppUrlOpenListener?.(({ url }) => receive(url, 'app_url_open'))).then((handle) => { listenerHandle = handle })
    Promise.resolve(bridge.getLaunchUrl?.()).then((payload) => { if (!disposed && payload?.url) return receive(payload.url, 'launch_url') }).catch(() => {})
    return () => { disposed = true; removeListenerHandle(listenerHandle) }
  }, [diagnostic, handleRedirectCallback])

  useEffect(() => {
    if (callbackState.state !== 'completed' || isLoading || !isAuthenticated || !pendingRouteRef.current) return
    clearMemberLocalLogoutMarker()
    diagnostic('auth_callback_session_settled', { authenticated: true })
    console.info('[members-auth-callback]', {
      event: 'callback_lifecycle_settled',
      authenticated: true,
      routeKey: pendingRouteRef.current,
      reason: 'authenticated',
    })
    navigate(pendingRouteRef.current, { replace: true })
    pendingRouteRef.current = null
  }, [callbackState.state, diagnostic, isAuthenticated, isLoading, navigate])

  return null
}

function MemberAuthCallback() {
  const { isLoading, isAuthenticated, error, handleRedirectCallback } = useAuth0()
  const location = useLocation()
  const navigate = useNavigate()
  const manualCallbackInFlightRef = useRef(false)
  const historyStateUsrPresent = Boolean(window.history.state && window.history.state.usr)
  const locationStatePresent = Boolean(location.state)
  const state =
    (window.history.state && window.history.state.usr) || location.state
  const clubCodeFromState =
    state && state.appState && state.appState.clubCode
  const lastClub = getStoredMemberClubCode() || null
  const targetClub = clubCodeFromState || lastClub
  const callbackSearchParams = useMemo(() => new URLSearchParams(location.search || ''), [location.search])
  const callbackHasCode = callbackSearchParams.has('code')
  const callbackHasState = callbackSearchParams.has('state')

  const rawStoredMobileCallbackUrl = useMemo(() => {
    if (typeof window === 'undefined') return ''
    const raw = window.sessionStorage?.getItem(MOBILE_AUTH_CALLBACK_INFLIGHT_KEY)
    if (!raw) return ''
    try {
      const parsed = JSON.parse(raw)
      return String(parsed?.rawUrl || '')
    } catch {
      return ''
    }
  }, [location.pathname, location.search])

  console.info(
    `[members-auth0-debug] MemberAuthCallback render isLoading=${String(isLoading)} isAuthenticated=${String(isAuthenticated)} pathname=${location.pathname} search=${location.search} historyStateUsrPresent=${String(historyStateUsrPresent)} locationStatePresent=${String(locationStatePresent)} clubCodeFromState=${String(clubCodeFromState || '')} lastClub=${String(lastClub || '')} targetClub=${String(targetClub || '')}`
  )
  console.info(`[members-auth0-debug] MemberAuthCallback authError=${error?.message || ''}`)

  useEffect(() => {
    const shouldRunManualCallback =
      isCapacitorRuntime() &&
      location.pathname === '/auth/callback' &&
      (callbackHasCode || callbackSearchParams.has('error')) &&
      callbackHasState &&
      !isAuthenticated

    if (!shouldRunManualCallback) return
    if (manualCallbackInFlightRef.current) {
      console.info('[members-auth0-debug] manual handleRedirectCallback skipped reason=inflight_guard')
      return
    }

    manualCallbackInFlightRef.current = true

    const callbackUrlSource = rawStoredMobileCallbackUrl || window.location.href
    console.info('[members-auth0-debug] manual handleRedirectCallback start')

    let cancelled = false

    ;(async () => {
      try {
        const redirectResult = await handleRedirectCallback(callbackUrlSource)
        if (cancelled) return
        const appState = redirectResult?.appState || null
        console.info(
          `[members-auth0-debug] manual handleRedirectCallback appState=${JSON.stringify(appState)}`
        )
        console.info('[members-auth0-debug] manual handleRedirectCallback success')
        console.info('[members-auth0-debug] clearing callback inflight marker=true')
        clearMemberCallbackInflight()
        if (targetClub) {
          navigate(`/${targetClub}/dashboard`, { replace: true })
        } else {
          navigate('/app', { replace: true })
        }
      } catch (manualCallbackError) {
        if (cancelled) return
        console.info(
          `[members-auth0-debug] manual handleRedirectCallback error=${manualCallbackError?.message || String(manualCallbackError || '')}`
        )
      } finally {
        if (!cancelled) {
          manualCallbackInFlightRef.current = false
        }
      }
    })()

    return () => {
      cancelled = true
      manualCallbackInFlightRef.current = false
    }
  }, [
    callbackHasCode,
    callbackHasState,
    handleRedirectCallback,
    isAuthenticated,
    location.pathname,
    navigate,
    rawStoredMobileCallbackUrl,
    targetClub,
  ])

  useEffect(() => {
    console.info(
      `[members-auth0-debug] MemberAuthCallback effect isLoading=${String(isLoading)} isAuthenticated=${String(isAuthenticated)} pathname=${location.pathname} search=${location.search} historyStateUsrPresent=${String(historyStateUsrPresent)} locationStatePresent=${String(locationStatePresent)} clubCodeFromState=${String(clubCodeFromState || '')} lastClub=${String(lastClub || '')} targetClub=${String(targetClub || '')}`
    )
    console.info(`[members-auth0-debug] MemberAuthCallback authError=${error?.message || ''}`)

    if (isLoading) {
      console.info('[members-auth0-debug] branch=return_waiting_for_isLoading')
      return
    }

    if (isAuthenticated && targetClub) {
      console.info(
        `[members-auth0-debug] branch=navigate_targetClub value=/${targetClub}/dashboard`
      )
      console.info('[members-auth0-debug] clearing callback inflight marker=true')
      clearMemberCallbackInflight()
      navigate(`/${targetClub}/dashboard`, { replace: true })
    } else if (isAuthenticated && (state?.appState?.mobileBootstrap || isCapacitorRuntime())) {
      console.info('[members-auth0-debug] branch=navigate_mobile_app value=/app')
      console.info('[members-auth0-debug] clearing callback inflight marker=true')
      clearMemberCallbackInflight()
      navigate('/app', { replace: true })
    } else if (isAuthenticated) {
      // As a last resort, send them to a generic “not found” page.
      console.info('[members-auth0-debug] branch=navigate_root value=/')
      console.info('[members-auth0-debug] clearing callback inflight marker=true')
      clearMemberCallbackInflight()
      navigate('/', { replace: true })
    } else {
      console.info('[members-auth0-debug] branch=no_navigation_taken')
    }
  }, [
    isLoading,
    isAuthenticated,
    error,
    navigate,
    location.pathname,
    location.search,
    location.state,
    historyStateUsrPresent,
    locationStatePresent,
    callbackHasCode,
    callbackHasState,
    clubCodeFromState,
    lastClub,
    targetClub,
    state,
  ])

  return <div className="member-loading">Finishing sign-in…</div>
}

function MembersAuth0DebugObserver() {
  const { isLoading, isAuthenticated, error, user, getAccessTokenSilently } = useAuth0()
  const location = useLocation()
  const tokenAttempted = useRef(false)
  const tokenOutcome = useRef({ succeeded: false, safeErrorCode: null })
  const clubCode = getStoredMemberClubCode()
  const organization = getStoredMemberOrg() || user?.org_id || ''
  const routeKey = location.pathname.slice(0, 80)

  useEffect(() => {
    console.info('[members-auth0]', { event: 'auth_provider_instance_created' })
  }, [])

  useEffect(() => {
    console.info(`[members-auth0-debug] auth isLoading changed value=${String(isLoading)}`)
  }, [isLoading])

  useEffect(() => {
    console.info(
      `[members-auth0-debug] auth isAuthenticated changed value=${String(isAuthenticated)}`
    )
  }, [isAuthenticated])

  useEffect(() => {
    console.info(
      `[members-auth0-debug] auth-state isLoading=${String(isLoading)} isAuthenticated=${String(isAuthenticated)} pathname=${location.pathname} search=${location.search}`
    )
    console.info(`[members-auth0-debug] authError=${error?.message || ''}`)
  }, [isLoading, isAuthenticated, location.pathname, location.search, error])

  useEffect(() => {
    const state = { isCapacitor: isCapacitorRuntime(), authLoading: isLoading, authenticated: isAuthenticated, userPresent: Boolean(user), subjectPresent: Boolean(user?.sub), organisationPresent: Boolean(organization), tokenAttempted: tokenAttempted.current, tokenSucceeded: tokenOutcome.current.succeeded, routeKey }
    const safeErrorCode = tokenOutcome.current.safeErrorCode || (isLoading ? 'auth_loading' : !isAuthenticated ? 'not_authenticated' : !user ? 'missing_user' : !user.sub ? 'missing_subject' : !organization ? 'missing_organisation' : 'none')
    void emitAuthBootstrapDiagnostic(clubCode, { event: 'auth_state_changed', ...state, safeErrorCode }, { onceKey: `state:${isLoading}:${isAuthenticated}:${Boolean(user)}:${Boolean(user?.sub)}:${Boolean(organization)}:${routeKey}` })
    if (!isLoading) void emitAuthBootstrapDiagnostic(clubCode, { event: 'auth_loading_finished', ...state, safeErrorCode }, { onceKey: `loading-finished:${isAuthenticated}:${Boolean(user?.sub)}` })
  }, [clubCode, isAuthenticated, isLoading, organization, routeKey, user])

  useEffect(() => {
    if (isLoading || tokenAttempted.current || !isAuthenticated || !clubCode || !user?.sub || !organization || typeof getAccessTokenSilently !== 'function') return
    tokenAttempted.current = true
    const began = performance.now()
    const state = { isCapacitor: isCapacitorRuntime(), authLoading: false, authenticated: true, userPresent: true, subjectPresent: true, organisationPresent: true, tokenAttempted: true, tokenSucceeded: false, routeKey }
    const authorizationParams = { audience: AUTH0_AUDIENCE, organization }
    void emitAuthBootstrapDiagnostic(clubCode, { event: 'auth_token_attempt_started', ...state }, { onceKey: `token-start:${clubCode}:${user.sub}` })
    const timeout = new Promise((_, reject) => setTimeout(() => reject(Object.assign(new Error('timeout'), { code: 'timeout' })), 5000))
    Promise.race([getAccessTokenSilently({ authorizationParams }), timeout]).then(() => {
      tokenOutcome.current = { succeeded: true, safeErrorCode: null }
      void emitAuthBootstrapDiagnostic(clubCode, { event: 'auth_token_attempt_succeeded', ...state, tokenSucceeded: true, durationMs: performance.now() - began }, { onceKey: `token-success:${clubCode}:${user.sub}` })
    }).catch((tokenError) => {
      const safeCode = classifyTokenError(tokenError) || safeAuthErrorCode(tokenError)
      tokenOutcome.current = { succeeded: false, safeErrorCode: safeCode }
      void emitAuthBootstrapDiagnostic(clubCode, { event: 'auth_token_attempt_failed', ...state, safeErrorCode: safeCode, durationMs: performance.now() - began }, { onceKey: `token-failed:${clubCode}:${user.sub}` })
    })
  }, [clubCode, getAccessTokenSilently, isAuthenticated, isLoading, organization, routeKey, user?.sub])

  return null
}

function ClubPortalContainer({ redirectUri, setMemberOrgId }) {
  const { clubCode } = useParams()
  const navigate = useNavigate()
  const { logout: clearAuth0Session } = useAuth0()
  const invalidResetRef = useRef(null)
  const [validationRevision, setValidationRevision] = useState(0)
  const cachedClubDisplay = useMemo(() => readClubDisplayCache(clubCode), [clubCode])
  const [settings, setSettings] = useState(() => cachedClubDisplay?.settings || null)
  const [settingsLoading, setSettingsLoading] = useState(() => !cachedClubDisplay?.settings)
  const [settingsError, setSettingsError] = useState(null)
  const [config, setConfig] = useState(() => cachedClubDisplay?.config || null)
  const [configLoading, setConfigLoading] = useState(() => !cachedClubDisplay?.config)
  const [configError, setConfigError] = useState(null)

  const handleInvalidClub = useCallback(async () => {
    if (invalidResetRef.current) return invalidResetRef.current
    const reset = resetInvalidMemberClub({
      clubCode,
      clearAuthOrganization: async () => {
        setMemberOrgId?.(null)
        // Clear Auth0's local, organisation-scoped token state without ending the reusable SSO session.
        await clearAuth0Session({ openUrl: false }).catch(() => {})
      },
    }).then((didReset) => {
      if (didReset) {
        navigate('/app', {
          replace: true,
          state: { clubUnavailable: true },
        })
      }
      return didReset
    }).finally(() => {
      invalidResetRef.current = null
    })
    invalidResetRef.current = reset
    return reset
  }, [clearAuth0Session, clubCode, navigate, setMemberOrgId])

  useEffect(() => {
    if (!isCapacitorRuntime()) return undefined
    let disposed = false
    let subscription
    getMemberMobileAuthBridge().addAppStateChangeListener?.(({ isActive }) => {
      if (isActive && !disposed) setValidationRevision((value) => value + 1)
    }).then((listener) => { subscription = listener }).catch(() => {})
    return () => {
      disposed = true
      subscription?.remove?.()
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    async function loadConfig() {
      const endRequest = startBootstrapSpan('organisation_resolution', { clubCode, category: 'club_config' })
      if (!config) setConfigLoading(true)
      setConfigError(null)
      try {
        const base = getApiBase()
        const res = await fetch(`${base}/api/public/club-config/${clubCode}`)
        if (await readInvalidClubResponse(res)) {
          if (!cancelled) await handleInvalidClub()
          return
        }
        if (!res.ok) {
          throw new Error('failed_to_fetch')
        }
        const json = await res.json()
        if (!cancelled) {
          setConfig(json.data)
          writeClubDisplayCache(clubCode, { config: json.data })
        }
        endRequest({ status: 'success' })
      } catch (err) {
        endRequest({ status: 'failed', reason: 'request_failed' })
        if (!cancelled) {
          setConfigError(err)
        }
      } finally {
        if (!cancelled) {
          setConfigLoading(false)
        }
      }
    }

    if (clubCode) {
      loadConfig()
    }

    return () => {
      cancelled = true
    }
  }, [clubCode, handleInvalidClub, validationRevision])


  useLayoutEffect(() => {
    applyPortalTheme({ accentColor: DEFAULT_MEMBERS_PORTAL_ACCENT, themeMode: 'dark' })
  }, [])

  useEffect(() => {
    const orgId = config?.memberPortalOrgId || config?.memberPortalOrgName || null
    if (orgId) {
      setStoredMemberOrg(orgId)
      setMemberOrgId?.(orgId)
    } else {
      clearStoredMemberOrg()
      setMemberOrgId?.(null)
    }
  }, [config?.memberPortalOrgId, config?.memberPortalOrgName, setMemberOrgId])

  useEffect(() => {
    let cancelled = false
    async function loadSettings() {
      const endRequest = startBootstrapSpan('portal_configuration_request', { clubCode, category: 'settings' })
      if (!settings) setSettingsLoading(true)
      setSettingsError(null)
      try {
        const base = getApiBase()
        const res = await fetch(`${base}/api/members-portal/${clubCode}/config`)
        if (await readInvalidClubResponse(res)) {
          if (!cancelled) await handleInvalidClub()
          return
        }
        if (!res.ok) {
          throw new Error('failed_to_fetch')
        }
        const json = await res.json()
        if (!cancelled) {
          const data = json.data || {}
          setSettings(data)
          writeClubDisplayCache(clubCode, { settings: data })
          applyPortalTheme({
            accentColor: data.accentColor || DEFAULT_MEMBERS_PORTAL_ACCENT,
            themeMode: data.themeMode || 'dark',
          })
          const resolvedOverlay = resolveHeroOverlayOpacity(data.heroOverlayOpacity)
          document.documentElement.style.setProperty('--mp-hero-overlay-opacity', String(Number(resolvedOverlay.toFixed(2))))
        }
        endRequest({ status: 'success' })
      } catch (err) {
        endRequest({ status: 'failed', reason: 'request_failed' })
        if (!cancelled) {
          setSettingsError(err)
        }
      } finally {
        if (!cancelled) {
          setSettingsLoading(false)
        }
      }
    }

    if (clubCode) {
      loadSettings()
    }

    return () => {
      cancelled = true
    }
  }, [clubCode, handleInvalidClub, validationRevision])

  useLivePortalTheme({
    clubCode,
    enabled: Boolean(clubCode && settings),
    initialAccentColor: settings?.accentColor || settings?.accent_color || DEFAULT_MEMBERS_PORTAL_ACCENT,
    initialThemeMode: settings?.themeMode || 'dark',
    initialHeroOverlayOpacity: settings?.heroOverlayOpacity ?? settings?.hero_overlay_opacity,
    initialUpdatedAt: settings?.updatedAt || null,
  })

  const branding = useMemo(
    () => ({
      clubName: settings?.clubName || clubCode,
      timeZone: settings?.clubCode === clubCode ? settings.timeZone : undefined,
      logoUrl: settings?.clubLogoUrl || null,
      primary: settings?.accentColor || settings?.accent_color || DEFAULT_MEMBERS_PORTAL_ACCENT,
    }),
    [clubCode, settings?.clubCode, settings?.timeZone, settings?.clubName, settings?.clubLogoUrl, settings?.accentColor, settings?.accent_color]
  )

  if ((configLoading || settingsLoading) && (!config || !settings)) {
    if (isCapacitorRuntime() && getStoredMemberClubCode() === clubCode) {
      return (
        <MemberThemeProvider branding={branding}>
          <MemberLayout branding={branding} clubCode={clubCode} member={null} managedActivities={[]} hasVotingAccess={false} isMobileApp>
            <div className="mp-panel" aria-busy="true"><div className="mp-panel__body">Restoring your club…</div></div>
          </MemberLayout>
        </MemberThemeProvider>
      )
    }
    return <div className="member-loading">Loading portal…</div>
  }

  if (shouldBlockForClubConfigError({ configError, config })) {
    return <div className="member-loading error">Unable to load club configuration.</div>
  }

  if (settingsError && !settings) {
    return <div className="member-loading error">Unable to load portal settings.</div>
  }

  const memberPortalEnabled = Boolean(config?.memberPortalEnabled)

  if (!memberPortalEnabled || !config) {
    return (
      <MemberThemeProvider branding={branding}>
        <ComingSoon clubName={branding.clubName} logoUrl={branding.logoUrl} heroImageUrl={settings?.heroImageUrl || settings?.hero_image_url} />
      </MemberThemeProvider>
    )
  }

  if (!config?.auth0Domain || !config?.auth0ClientId || !config?.memberPortalOrgName) {
    return <div className="member-loading error">Portal configuration incomplete.</div>
  }

  return (
    <AuthenticatedMemberPortal
      config={config}
      settings={settings}
      branding={branding}
      clubCode={clubCode}
      redirectUri={redirectUri}
      setMemberOrgId={setMemberOrgId}
    />
  )
}

export default function MemberPortalApp() {
  useEffect(() => {
    document.documentElement.classList.add('members-scrollbars')
    return () => document.documentElement.classList.remove('members-scrollbars')
  }, [])
  const [memberOrgId, setMemberOrgId] = useState(() =>
    getStoredMemberOrg() || null
  )

  const redirectUri = useMemo(
    () => {
      if (isCapacitorRuntime()) {
        return resolveMembersMobileRedirectUri({
          explicitRedirectUri: MEMBERS_MOBILE_CALLBACK_URI,
          auth0Domain: AUTH0_DOMAIN,
        })
      }

      return AUTH0_MEMBER_WEB_REDIRECT_URI || `${window.location.origin}/auth/callback`
    },
    []
  )

  useEffect(() => {
    console.info(
      `[members-mobile-auth] resolved redirect_uri mode=${isCapacitorRuntime() ? 'mobile' : 'members-web'} value=${redirectUri}`
    )
  }, [redirectUri])

  // Provider configuration is immutable for the application lifetime. Organisation
  // is supplied per login/token request and must never recreate Auth0 ownership.
  const authorizationParams = useMemo(() => ({ redirect_uri: redirectUri, audience: AUTH0_AUDIENCE }), [redirectUri])

  if (!AUTH0_DOMAIN || !AUTH0_CLIENT_ID) {
    return <div className="member-loading error">Auth0 not configured.</div>
  }

  return (
    <Auth0Provider
      domain={AUTH0_DOMAIN}
      clientId={AUTH0_CLIENT_ID}
      authorizationParams={authorizationParams}
      cacheLocation="localstorage"
      useRefreshTokens
      useRefreshTokensFallback={false}
      skipRedirectCallback={isCapacitorRuntime()}
    >
      <NativeAuthCallbackCoordinator />
      <MembersAuth0DebugObserver />
      <Routes>
        <Route path="/vote/:clubCode/:motionId" element={<PublicVotingPage />} />
        <Route path="/auth/callback" element={<MemberAuthCallback />} />
        <Route path="/post-logout" element={<MemberPostLogoutRedirect />} />
        <Route path="/login" element={<MemberPortalLoginPage redirectUri={redirectUri} />} />
        <Route path="/app" element={<MobileAppBootstrap redirectUri={redirectUri} />} />
        <Route path="/privana-pay/checkout-opening" element={<MemberPrivanaPayCheckoutOpeningPage />} />
        <Route path="/privana-pay/payment-received" element={<MemberPrivanaPayResultPage state="received" />} />
        <Route path="/guest/event-payment/:token" element={<PublicEventGuestPaymentPage />} />
        <Route path="/privana-pay/payment-cancelled" element={<MemberPrivanaPayResultPage state="cancelled" />} />
        <Route path="/privana-pay/payment-declined" element={<MemberPrivanaPayResultPage state="declined" />} />
        <Route
          path="/"
          element={
            isCapacitorRuntime() ? (
              <Navigate to="/app" replace />
            ) : (
              <div className="member-loading">Page not found.</div>
            )
          }
        />
        <Route
          path="/:clubCode/*"
          element={<ClubPortalRoute redirectUri={redirectUri} setMemberOrgId={setMemberOrgId} />}
        />
        <Route path="*" element={<div className="member-loading">Page not found.</div>} />
      </Routes>
    </Auth0Provider>
  )
}
